import React, { Component, createContext, useContext } from 'react';
import EventMapEmitter from 'app/utils/EventMapEmitter';
import { Writable } from 'app/utils/types';
import { createStructuredSelector } from 'reselect';

export interface IBounds {
  readonly bottom: number;
  readonly height: number;
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly width: number;
}

export interface IPosition {
  readonly x: number;
  readonly y: number;
}

export interface IScrollSize {
  readonly scrollWidth: number;
  readonly scrollHeight: number;
}

export interface IScrollEventMap {
  scroll: Event;
}

/**
 * Anything that can emit scroll events
 */
interface ISourceEmitter<Type extends keyof IScrollEventMap> {
  addEventListener(type: Type, handler: (event: IScrollEventMap[Type]) => void): void;
  removeEventListener(type: Type, handler: (event: IScrollEventMap[Type]) => void): void;
}

/**
 * Methods that need to be implemented by any concrete
 * implementations of `ScrollProvider`
 */
interface IScrollDelegates {
  /**
   * Scroll to x and or y component of position (whichever are
   * defined)
   */
  scrollTo(position: Partial<IPosition>): void;
  /**
   * Return size and position of the viewport (not its content)
   */
  getBounds(): IBounds;
  /**
   * Return how big is the content inside the scrolled element
   */
  getScrollSize(): IScrollSize;
  /**
   * Return how much the viewport is scrolled, vertically and
   * horizontally
   */
  getPosition(): IPosition;
  /**
   * Return the element with the given anchor id
   */
  findElementByAnchor(anchor: string): Element | null;
}

/**
 * Subscribe subset of event emitter
 */
export interface IScrollCtrlContext extends
IScrollDelegates,
Pick<EventMapEmitter<IScrollEventMap>, 'bindEvents' | 'unbindEvents'> {
  scrollToAnchor(anchor: string, withPrefix: boolean, offset?: number): boolean;
  scrollToElement(element: Element, offset?: number): void;
  scrollBy(offsets: Partial<IPosition>): void;
  scrollId: string;
  getPrefixedScrollId: (id: string) => string;
  setVideoLazyLoaded: () => void,
}

const ScrollCtrlContext = createContext<IScrollCtrlContext>({
  scrollTo: () => {},
  scrollBy: () => {},
  scrollToAnchor: () => false,
  scrollToElement: () => {},
  getPrefixedScrollId: (id) => id,
  getBounds: () => ({ top: 0, left: 0, right: 0, bottom: 0, width: 0, height: 0 }),
  getScrollSize: () => ({ scrollWidth: 0, scrollHeight: 0 }),
  getPosition: () => ({ x: 0, y: 0 }),
  findElementByAnchor: () => null,
  bindEvents: () => {},
  unbindEvents: () => {},
  scrollId: '',
  setVideoLazyLoaded: () => {},
});

interface IState {
  videoLazyLoaded: boolean;
}

const ScrollContextState = createContext<IState>({
  videoLazyLoaded: false,
});

export interface IScrollProviderProps extends IScrollDelegates {
  scrollEmitter?: ISourceEmitter<'scroll'>;
  scrollId?: string;
}

/**
 * Subscribes to a scroll-event-emitting object and reemits those
 * events to all of its own subscribers. This means the underlying
 * origin of the events can change but subscribers don't need to
 * cater to that
 */
export class ScrollProvider extends Component<IScrollProviderProps, IState> {
  private readonly emitter = new EventMapEmitter<IScrollEventMap>();
  // videoLazyLoaded is currently used to make sure we don't try to scroll
  // to content until the video (which is loaded lazily) has finished loading.
  // In the future we may want to generalize this logic to handle other things
  // we might want to wait for.
  public state = {
    videoLazyLoaded: false,
  };

  private readonly reemitters = {
    scroll: (event: Event) => this.emitter.emit('scroll', event),
  };

  public componentDidMount() {
    this.updateSourceEmitters(this.props);
  }

  public componentDidUpdate(oldProps: IScrollProviderProps) {
    this.updateSourceEmitters(this.props, oldProps);
  }

  public componentWillUnmount() {
    this.updateSourceEmitters(undefined, this.props);
  }

  public render() {
    const { children } = this.props;

    return <ScrollCtrlContext.Provider value={this.getContextInterface(this.props)}>
      <ScrollContextState.Provider value={this.state}>
        {children}
      </ScrollContextState.Provider>
    </ScrollCtrlContext.Provider>;
  }

  // Create new context interface so consumers re-render when the provided
  // functions change. This is mainly needed for ElementScrollProvider, so
  // children re-render when the scroll element is mounted.
  private getContextInterface = createStructuredSelector<IScrollProviderProps, IScrollCtrlContext>({
    bindEvents:          (props) => this.bindEvents,
    unbindEvents:        (props) => this.unbindEvents,
    scrollTo:            (props) => props.scrollTo,
    scrollBy:            (props) => this.scrollBy,
    scrollToAnchor:      (props) => this.scrollToAnchor,
    scrollToElement:     (props) => this.scrollToElement,
    getPrefixedScrollId: (props) => this.getPrefixedScrollId,
    getBounds:           (props) => props.getBounds,
    getScrollSize:       (props) => props.getScrollSize,
    getPosition:         (props) => props.getPosition,
    findElementByAnchor: (props) => props.findElementByAnchor,
    scrollId:            (props) => this.scrollId,
    setVideoLazyLoaded:  (props) => this.setVideoLazyLoaded,
  });

  private bindEvents = this.emitter.bindEvents.bind(this.emitter);
  private unbindEvents = this.emitter.unbindEvents.bind(this.emitter);

  private get scrollId() {
    return this.props.scrollId ? this.props.scrollId : '';
  }

  /**
   * Get a scroll id with the correct prefix
   * @param id the basic id without a prefix
   * @returns the id with the correct prefix
   */
  private getPrefixedScrollId = (id: string) => {
    return `${this.scrollId}${id}`;
  };

  private setVideoLazyLoaded = () => {
    this.setState({ videoLazyLoaded: true });
  };

  private scrollToAnchor = (anchor: string, withPrefix: boolean, offset?: number): boolean => {
    // We expect our anchors to have only alphanumeric characters, dashes, and underscores.
    const validSelectorRegExp = /^#[-\w]+$/;

    // We can't have multiple HTML elements with the same ids, so we use a prefix
    // to differentiate in cases like the editor, where we want to use the anchor
    // to scroll to components on both panes.
    const anchorWithPrefix = withPrefix ? `${this.props.scrollId}${anchor}` : anchor;
    if (anchor.length <= 1 ||
      !validSelectorRegExp.test(`#${anchorWithPrefix}`)) {
      return false;
    }

    const anchorElement = this.props.findElementByAnchor(anchorWithPrefix);

    if (anchorElement) {
      this.scrollToElement(anchorElement, offset);
      return true;
    }
    return false;
  };

  private scrollToElement = (element: Element, offset?: number) => {
    const { top: viewportTop } = this.props.getBounds();
    const { y: scrollY } = this.props.getPosition();
    const { top } = element.getBoundingClientRect();
    this.props.scrollTo({ y: scrollY + top - viewportTop + (offset || 0) });
  };

  private scrollBy = (offsets: Partial<IPosition>) => {
    const { x, y } = this.props.getPosition();

    const gotoPosition: Partial<Writable<IPosition>> = {};

    if (offsets.x) {
      gotoPosition.x = x + offsets.x;
    }

    if (offsets.y) {
      gotoPosition.y = y + offsets.y;
    }

    this.props.scrollTo(gotoPosition);
  };

  private updateSourceEmitters(newProps?: IScrollProviderProps, oldProps?: IScrollProviderProps) {
    this.updateSourceEmitter(
      'scroll',
      newProps && newProps.scrollEmitter,
      oldProps && oldProps.scrollEmitter,
    );
  }

  private updateSourceEmitter<
    Type extends keyof IScrollEventMap,
    Emitter extends ISourceEmitter<Type>
  >(type: Type, newEmitter?: Emitter, oldEmitter?: Emitter) {
    if (newEmitter !== oldEmitter) {
      if (oldEmitter) {
        oldEmitter.removeEventListener(type, this.reemitters[type]);
      }

      if (newEmitter) {
        newEmitter.addEventListener(type, this.reemitters[type]);
      }
    }
  }
}

export const ScrollCtrlConsumer = ScrollCtrlContext.Consumer;
export const useScrollCtrl = () => useContext(ScrollCtrlContext);

export const ScrollContextStateConsumer = ScrollContextState.Consumer;
export const useScrollContextState = () => useContext(ScrollContextState);
